import { createSelector } from 'reselect';

import { APPOINTMENT_TASK_KEY_STATUS, APPOINTMENT_TASK_TYPES } from 'constants/AppointmentConstants';
import * as appointments from 'modules/appointments/selectors/general';
import { getLockerById as getLockerSelector } from 'modules/lockers/selectors';

import * as Utils from './utils';

const getState = state => state.appointmentTask;

const getId = (_, id) => id;

const getIds = (_, ids) => ids;

const getRoot = state => state;

export const getAllAppointmentTask = createSelector(getState, state => state.data.byId);

export const getAppointmentTaskById = createSelector(getAllAppointmentTask, getId, (state, id) => state?.[id]);

export const getAppointmentTasksByIds = createSelector(
  getAllAppointmentTask,
  getIds,
  (collection, ids = []) => ids?.map(id => collection?.[id]) ?? []
);

export const getTaskByAppointmentTaskId = createSelector(
  getAppointmentTaskById,
  appointmentTask => appointmentTask?.task
);

export const isLoading = createSelector(getState, state => state.data.isLoading);

export const isLoadingTask = createSelector(getState, state => state.ui.isLoadingTask);

export const isLoadingBookingTask = createSelector(getState, state => state.ui.isLoadingBookingTask);

export const isResolving = createSelector(getState, state => state.ui.isResolving);

const getLocker = createSelector(getRoot, getAppointmentTaskById, (state, appointment) => {
  const { vaultId } = appointment ?? {};
  if (vaultId) {
    return getLockerSelector(state, vaultId);
  }
  return undefined;
});

const getMode = createSelector(
  getAppointmentTaskById,
  getLocker,
  Utils.getMode
);

const getAppointment = createSelector(getRoot, getAppointmentTaskById, (state, appointmentTask = {}) => {
  const { appointmentId } = appointmentTask;
  return appointments.getById(state, appointmentId) ?? {};
});

const getSiblingsTasks = createSelector(getRoot, getAppointment, (state, appointment = {}) => {
  const { appointmentTasksIds = [] } = appointment;
  return getAppointmentTasksByIds(state, appointmentTasksIds);
});

const hasRentalTask = createSelector(getSiblingsTasks, (tasks = []) =>
  tasks.some(({ task }) => task.type === APPOINTMENT_TASK_TYPES.RENTAL_VEHICLE));

const isNextAppointment = createSelector(
  getAppointmentTaskById,
  getLocker,
  (appointmentTask, locker) => appointmentTask?.nextAppointment ?? locker?.nextAppointmentTask === appointmentTask?.id
);

export const getFormattedAppointmentTask = createSelector(
  getAppointmentTaskById,
  getLocker,
  getMode,
  hasRentalTask,
  isNextAppointment,
  (appointmentTask = {}, locker, mode, hasRental, isNext) => {
    const { task } = appointmentTask;
    const isPickup = Utils.isPickupTask(task);
    const isDropOff = Utils.isDropOffTask(task);

    const isPreparation = isNext && Utils.isPreparationMode(mode);
    const isVerification = isNext && Utils.isVerificationMode(mode);
    const isUnpreparation = isNext && Utils.isUnpreparationMode(mode);

    const { bookingStatus = Utils.getBookingStatus(appointmentTask) } = locker ?? {};

    return appointmentTask
      ? {
        mode,
        locker,
        isPickup,
        hasRental,
        isDropOff,
        isPreparation,
        isVerification,
        appointmentTask,
        isUnpreparation,
        isNextAppointment: isNext,
        isPreparable: !!appointmentTask.preparable,
        taskDescription: Utils.getTaskDescription(task, mode, hasRental),
        rights: {
          showRental: isNext && hasRental && Utils.canShowRental(task, mode),
          canSetRentalKey: isNext && hasRental && Utils.canSetRentalKey(task, mode),
          canSetCustomerKey: isNext && Utils.canSetCustomerKey(appointmentTask, locker),
          canUnprepare: Utils.canUnprepare(appointmentTask?.status, bookingStatus),
        },
        workflow: {
          mustPutKeyInLocker: isNext && Utils.mustPutKeyInLocker(task, mode, hasRental),
          mustControlLocker: isNext && Utils.mustControlLocker(task, mode, hasRental),
          mustGetKeyFromLocker: isNext && Utils.mustGetKeyFromLocker(task, mode, hasRental),
        },
      }
      : undefined;
  }
);

/**
 * @return APPOINTMENT_TASK_KEY_STATUS
 */
export const getDefaultVehicleKeysByAppointmentTaskId = createSelector(
  getFormattedAppointmentTask,
  ({ task = {}, isPreparation, isDropOff, isVerification }) => {
    let vehicleKeys = task?.vehicleKeys;
    const isPreparationAndPickup = isPreparation && !isDropOff;
    if (isPreparationAndPickup) {
      vehicleKeys = APPOINTMENT_TASK_KEY_STATUS.DROPPED;
    }

    if (isVerification) {
      vehicleKeys = APPOINTMENT_TASK_KEY_STATUS.PICKED;
    }
    return vehicleKeys;
  }
);

/**
 * @return APPOINTMENT_TASK_KEY_STATUS
 */
export const getDefaultRentalKeysByAppointmentTaskId = createSelector(
  getFormattedAppointmentTask,
  ({ task = {}, isPreparation, isDropOff, isVerification, hasRental }) => {
    const isPreparationAndPickup = isPreparation && !isDropOff;
    let rentalKeys = task?.rentalKeys;
    if (isPreparationAndPickup) {
      rentalKeys = hasRental ? APPOINTMENT_TASK_KEY_STATUS.DROPPED : rentalKeys;
    }

    if (isVerification) {
      rentalKeys = hasRental ? APPOINTMENT_TASK_KEY_STATUS.PICKED : rentalKeys;
    }
    return rentalKeys;
  }
);

export const showAddDriverLicence = createSelector(getState, state => state.ui.showAddDriverLicence);

const getAllServiceHeaders = createSelector(getState, ({ data }) => data.serviceHeaders);

export const getServiceHeader = createSelector(getAllServiceHeaders, getId, (serviceHeaders, id) => serviceHeaders[id]);
